import React, { useEffect, useState } from 'react';
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { FaCopy, FaBitcoin, FaCheckCircle, FaExclamationTriangle, FaEthereum } from 'react-icons/fa';
import { SiTether } from 'react-icons/si';
import UploadPage from '../componets/Essentials/UploadPage';
import { auth, db } from '../../Config/Config';
import Modal from '../../components/Essentials/Modal';
import './Deposit.css';

const Deposite = () => {
  const [availableWallets, setAvailableWallets] = useState([]);
  const [selectedCrypto, setSelectedCrypto] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [proceed, setProceed] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [showCopyAlert, setShowCopyAlert] = useState(false);
  const [status, setStatus] = useState('');
  const MINIMUM_DEPOSIT = 100;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const showModal = (message) => {
    setModalMessage(message);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalMessage('');
  };

  // Fetch all available wallets from admin
  useEffect(() => {
    const fetchAvailableWallets = async () => {
      try {
        const walletCollection = collection(db, 'wallets');
        const walletSnapshot = await getDocs(walletCollection);
        
        if (!walletSnapshot.empty) {
          const wallets = walletSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setAvailableWallets(wallets);
          
          // Auto-select first wallet if available
          if (wallets.length > 0) {
            setSelectedCrypto(wallets[0].walletType);
            setWalletAddress(wallets[0].walletAddress);
          }
        } else {
          setError('No wallets available. Please contact support.');
        }
      } catch (err) {
        console.error('Error fetching wallets:', err);
        setError('Failed to fetch wallet information');
      } finally {
        setLoading(false);
      }
    };

    fetchAvailableWallets();
  }, []);

  // Update wallet address when crypto selection changes
  const handleCryptoChange = (e) => {
    const selectedType = e.target.value;
    setSelectedCrypto(selectedType);
    
    const selectedWallet = availableWallets.find(
      wallet => wallet.walletType === selectedType
    );
    
    if (selectedWallet) {
      setWalletAddress(selectedWallet.walletAddress);
    }
  };

  // Get crypto icon based on type
  const getCryptoIcon = (cryptoType) => {
    const type = cryptoType?.toUpperCase();
    if (type?.includes('BITCOIN')) return <FaBitcoin className="btc-deposit-detail-icon" />;
    if (type?.includes('ETHEREUM')) return <FaEthereum className="btc-deposit-detail-icon" />;
    if (type?.includes('USDT')) return <SiTether className="btc-deposit-detail-icon" />;
    return <FaBitcoin className="btc-deposit-detail-icon" />;
  };

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (user) {
        const uid = user.uid;
        const depositsCollection = collection(db, 'deposits');
        const userDepositsQuery = query(depositsCollection, where('uid', '==', uid));

        const unsubscribeDeposits = onSnapshot(
          userDepositsQuery,
          (snapshot) => {
            const depositsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            if (depositsData.length > 0) {
              const latestDeposit = depositsData[0];
              setAmount(latestDeposit.amount || '0');
              setStatus(latestDeposit.status || 'No status');
            } else {
              setAmount('0');
              setStatus('None');
            }
          },
          (error) => {
            console.error('Error fetching deposits: ', error);
            setError('Failed to fetch deposit data');
          }
        );

        return () => unsubscribeDeposits();
      } else {
        setError('User not authenticated');
        setLoading(false);
      }
    });
  
    return () => unsubscribeAuth();
  }, []);
  
  const handleProceed = () => {
    if (!selectedCrypto) {
      setError('Please select a cryptocurrency');
      return;
    }
    if (amount < MINIMUM_DEPOSIT) {
      setShowAlert(true);
    } else if (walletAddress && amount >= MINIMUM_DEPOSIT) {
      setProceed(true);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(walletAddress)
      .then(() => {
        setShowCopyAlert(true);
        setTimeout(() => setShowCopyAlert(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  const getInstructionNote = () => {
    return (
      <div className="btc-deposit-instruction-container">
        <h3 className="btc-deposit-instruction-title">Important Notes</h3>
        <ul className="btc-deposit-instruction-list">
          <li className="btc-deposit-instruction-item">
            <FaCheckCircle className="btc-deposit-instruction-icon" />
            <span>Send only {selectedCrypto} to this address</span>
          </li>
          <li className="btc-deposit-instruction-item">
            <FaExclamationTriangle className="btc-deposit-instruction-icon btc-deposit-warning" />
            <span>Sending any other coin may result in permanent loss</span>
          </li>
          <li className="btc-deposit-instruction-item">
            <FaCheckCircle className="btc-deposit-instruction-icon" />
            <span>Account will be credited after confirmation</span>
          </li>
          <li className="btc-deposit-instruction-item">
            <FaCheckCircle className="btc-deposit-instruction-icon" />
            <span>For verification purposes, please upload the payment proof</span>
          </li>
        </ul>
      </div>
    );
  };

  if (loading) return (
    <div className="btc-deposit-loading-screen">
      <div className="btc-deposit-loading-spinner"></div>
      <p>Loading wallet information...</p>
    </div>
  );
  
  if (error) return (
    <div className="btc-deposit-error-screen">
      <FaExclamationTriangle className="btc-deposit-error-icon" />
      <p>{error}</p>
    </div>
  );

  return (
    <div className="btc-deposit-page">
      <header className="btc-deposit-header">
        <h1 className="btc-deposit-title">Cryptocurrency Deposit</h1>
        <p className="btc-deposit-subtitle">Securely fund your account with cryptocurrency</p>
      </header>

      <div className="btc-deposit-content">
        {/* Transaction Status Card */}
        <div className="btc-deposit-status-card">
          <div className="btc-deposit-status-card-header">
            {getCryptoIcon(selectedCrypto)}
            <h3 className="btc-deposit-status-card-title">Transaction Details</h3>
          </div>
          <div className="btc-deposit-status-card-body">
            <div className="btc-deposit-status-card-row">
              <span className="btc-deposit-status-card-label">Amount:</span>
              <span className="btc-deposit-status-card-amount">${amount}</span>
            </div>
            <div className="btc-deposit-status-card-row">
              <span className="btc-deposit-status-card-label">Status:</span>
              <span className={`btc-deposit-status-card-value btc-deposit-status-${status}`}>
                {status === "approved" ? "None" : status}
              </span>
            </div>
          </div>
        </div>

        {/* Deposit Form/Summary */}
        <div className="btc-deposit-main-card">
          {!proceed ? (
            <>
              <div className="btc-deposit-card-header">
                <h2 className="btc-deposit-card-title">Enter Deposit Details</h2>
                <p className="btc-deposit-card-subtitle">Minimum deposit: <span>${MINIMUM_DEPOSIT}</span></p>
              </div>
              
              <div className="btc-deposit-form">
                {/* Crypto Selection Dropdown */}
                <div className="btc-deposit-form-group">
                  <label htmlFor="crypto" className="btc-deposit-form-label">Select Cryptocurrency</label>
                  <select
                    id="crypto"
                    value={selectedCrypto}
                    onChange={handleCryptoChange}
                    className="btc-deposit-form-input"
                    style={{ 
                      paddingLeft: '12px',
                      backgroundColor: '#1a1a1a',
                      color: '#ffffff',
                      border: '1px solid #333',
                      cursor: 'pointer'
                    }}
                  >
                    {availableWallets.map(wallet => (
                      <option 
                        key={wallet.id} 
                        value={wallet.walletType}
                        style={{
                          backgroundColor: '#1a1a1a',
                          color: '#ffffff',
                          padding: '10px'
                        }}
                      >
                        {wallet.walletType}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="btc-deposit-form-group">
                  <label htmlFor="amount" className="btc-deposit-form-label">Amount (USD)</label>
                  <div className="btc-deposit-input-group">
                    <span className="btc-deposit-input-prefix">$</span>
                    <input
                      id="amount"
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="btc-deposit-form-input"
                      placeholder="100.00"
                      min={MINIMUM_DEPOSIT}
                    />
                  </div>
                </div>

                <button
                  onClick={handleProceed}
                  disabled={!walletAddress || !amount || !selectedCrypto}
                  className={`btc-deposit-button ${!walletAddress || !amount || !selectedCrypto ? 'btc-deposit-disabled' : ''}`}
                >
                  Continue to Deposit
                </button>
              </div>
            </>
          ) : (
            <div className="btc-deposit-summary">
              <div className="btc-deposit-summary-header">
                <h2 className="btc-deposit-summary-title">Deposit Summary</h2>
                <p className="btc-deposit-summary-subtitle">Complete your deposit by sending {selectedCrypto}</p>
              </div>

              <div className="btc-deposit-summary-details">
                <div className="btc-deposit-detail-row">
                  <span className="btc-deposit-detail-label">Wallet Type:</span>
                  <span className="btc-deposit-detail-value">
                    {getCryptoIcon(selectedCrypto)}
                    {selectedCrypto}
                  </span>
                </div>
                <div className="btc-deposit-detail-row">
                  <span className="btc-deposit-detail-label">Amount:</span>
                  <span className="btc-deposit-detail-value">${amount}</span>
                </div>
              </div>

              <div className="btc-deposit-wallet-address-section">
                <h3 className="btc-deposit-address-title">Send to this {selectedCrypto} Address</h3>
                <div className="btc-deposit-address-container">
                  <div className="btc-deposit-address-value">{walletAddress}</div>
                  <button onClick={copyToClipboard} className="btc-deposit-copy-button">
                    <FaCopy className="btc-deposit-copy-icon" />
                    <span>Copy</span>
                  </button>
                </div>
              </div>

              {getInstructionNote()}
            </div>
          )}
        </div>

        {proceed && <UploadPage amount={amount} onShowModal={showModal} />}
      </div>

      {/* Modals */}
      {showAlert && (
        <div className="btc-deposit-modal-overlay">
          <div className="btc-deposit-modal">
            <div className="btc-deposit-modal-header btc-deposit-modal-error">
              <FaExclamationTriangle className="btc-deposit-modal-icon" />
              <h3 className="btc-deposit-modal-title">Minimum Deposit Required</h3>
            </div>
            <div className="btc-deposit-modal-body">
              <p>The minimum deposit amount is <strong>${MINIMUM_DEPOSIT}</strong>. Please enter a higher amount to proceed.</p>
            </div>
            <div className="btc-deposit-modal-footer">
              <button onClick={() => setShowAlert(false)} className="btc-deposit-modal-button">
                Understood
              </button>
            </div>
          </div>
        </div>
      )}

      {showCopyAlert && (
        <div className="btc-deposit-notification">
          <FaCheckCircle className="btc-deposit-notification-icon" />
          <span>Wallet address copied to clipboard!</span>
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title="Transaction Update"
        message={modalMessage}
      />
    </div>
  );
}

export default Deposite;